import Bugsnag from "@bugsnag/js";
import { AppleSignUp, AppleOauthResponse } from "../../models/Interfaces";
import { getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router-dom";
import { appSettingsAtom, writeOnlyTokensAtom, writeOnlyUserAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { errorLog } from "../../utils/logger";
import { STAY_LOGGED_IN_PW, REDIRECT_URI } from "../../globals";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SignInWithApple, SignInWithAppleOptions } from "@capacitor-community/apple-sign-in";
import React, { useEffect } from "react";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { updateCryptoKeyAndUserAndTokens } from "../../utils/auth";

// This redirect URL is different because it is configured for deep linking back to the app
// after the Apple Oauth flow is completed.
const ANDROID_REDIRECT_URI = "https://docket.care/apple-auth/callback";
const APPLE_CLIENT_ID = "com.foxhallwythe.docket.mobile.apple.signin";

export default function AppleSignIn({ onError }: { onError: (error: string) => void }) {
  const navigate = useNavigate();
  const [appSettings, setAppSettings] = useAtom(appSettingsAtom);
  const [user, setUser] = useAtom(writeOnlyUserAtom);
  const [tokens, setTokens] = useAtom(writeOnlyTokensAtom);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        // Handle Apple Sign In Redirect. This is only the case for Android
        // Due to the fact that the current Capacitor Apple Sign in Plugins
        // do not support Android.
        // Typical URL: https://docket.care/apple-auth/callback#code=1234567890&id_token=1234567890
        if (event.url.includes("apple-auth/callback")) {
          signInWithAppleAndroid(event.url);
        }
      });
    }
  }, []);

  const parseAppleFragment = (url: string) => {
    const fragment = url.split("#")[1]; // Get part after '#'
    if (!fragment) return {};

    const params = new URLSearchParams(fragment);
    const code = params.get("code") || undefined;
    const id_token = params.get("id_token") || undefined;

    return { code, id_token };
  };

  const signInWithAppleDocket = async (appleSignup: AppleSignUp) => {
    try {
      const loginResult = await getAPIClient().loginApple(appleSignup);

      if (!loginResult || !loginResult.tokens?.access) {
        // This really shouldn't happen
        const err = new Error("Did not get a login result or token");
        Bugsnag.notify(err);
        throw err;
      }

      // oauth users don't have a password....
      await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
      await setAppSettings({
        ...appSettings,
        email: loginResult.email || "",
      });
      // First param is the user ID, which we don't expose
      Bugsnag.setUser("", loginResult.email);
      if (
        !loginResult.dob ||
        !loginResult.legal_sex ||
        !loginResult.first_name ||
        !loginResult.last_name
      ) {
        navigate("/signup/accountinfo");
      } else {
        navigate("/home/search");
      }
    } catch (e: any) {
      errorLog(e);
      onError("Apple Sign In failed");
    }
  };

  const signInWithAppleAndroid = async (url: string) => {
    const { code, id_token } = parseAppleFragment(url);
    const appleSignup: AppleSignUp = {
      code: code || "",
      token: id_token || "",
      email: "noEmail@apple.com",
      first_name: "firstName",
      last_name: "lastName",
    };

    if (!code || !id_token) {
      // Either Apple OAuth is not formatting uri correctly or parsing logic is incorrect.
      Bugsnag.notify(new Error("No code or id_token found in Apple Sign In"));
    }

    await signInWithAppleDocket(appleSignup);
  };

  const signInWithAppleIOSWeb = async () => {
    let options: SignInWithAppleOptions = {
      clientId: APPLE_CLIENT_ID,
      redirectURI: REDIRECT_URI,
      scopes: "email name",
    };
    const response = await SignInWithApple.authorize(options);
    const appleSignup: AppleSignUp = {
      code: response.response.authorizationCode,
      token: response.response.identityToken,
      email: response.response.email ?? "noEmail@apple.com",
      first_name: response.response.givenName ?? "firstName",
      last_name: response.response.familyName ?? "lastName",
    };
    await signInWithAppleDocket(appleSignup);
  };

  const handleAppleLoginClick = async () => {
    const platform = Capacitor.getPlatform();
    if (platform === "ios" || platform === "web") {
      await signInWithAppleIOSWeb();
    } else if (platform === "android") {
      // Need to manually trigger the Oauth flow and handle the auth tokens via the event listener
      // to initiate token exchange.
      await Browser.open({
        url: `https://appleid.apple.com/auth/authorize?client_id=${APPLE_CLIENT_ID}&redirect_uri=${ANDROID_REDIRECT_URI}&response_type=code%20id_token&response_mode=fragment`,
      });
    }
  };

  return (
    <button
      data-testid="Sign-in-Apple"
      type="button"
      onClick={handleAppleLoginClick}
      className="!btn !flex-1 !bg-white !px-3 !py-8 max-md:!px-1 max-md:!py-2 max-md:!border-none"
    >
      <figure className="image w-[30px] mr-0 md:w-[40px] md:mr-[10px]">
        <img src="/assets/images/apple_button.png" alt="Sign in with Apple" />
      </figure>
      <span className="text-2xl font-normal max-md:hidden has-text-dark">Apple</span>
    </button>
  );
}
